import { DimensionUnit } from "@redotech/crdb-enums/product/dimension-unit";
import * as z from "zod";
import { InternationalCustomsCatalogRow } from "./list-international-customs-catalog";
import { newManufacturerSchema } from "./returns/update-return-product-customs";

const id = z.string().regex(/^\d{1,19}$/);
/** Same ceilings as the customs CSV import; weight is decimal(10, 2). */
const WEIGHT_MAX = 99_999_999.99;
const DIMENSION_MAX = 99_999;
const dimension = z.number().positive().max(DIMENSION_MAX);

/** One catalog row's merchant-editable customs values. An omitted field is
 * left as it is. Only weight, dimensions and manufacturerId can be cleared,
 * with null. */
export const updateInternationalCustomsCatalogProduct = {
  name: "updateInternationalCustomsCatalogProduct" as const,
  input: z.object({
    productId: id,
    /** 6 to 10 digits; dots and spaces between groups are kept as typed. */
    hsCode: z
      .string()
      .trim()
      .refine(
        (value) => /^\d{6,10}$/.test(value.replace(/[.\s-]/g, "")),
        "6 to 10 digits",
      )
      .optional(),
    /** What customs officers read; the customs form allows 45 characters. */
    customsDescription: z.string().trim().min(1).max(45).optional(),
    /** ISO 3166-1 alpha-2. */
    originCountry: z
      .string()
      .regex(/^[A-Z]{2}$/)
      .optional(),
    weight: z
      .object({
        value: z
          .number()
          .positive()
          .max(WEIGHT_MAX)
          .refine(
            (value) => Math.round(value * 100) / 100 === value,
            "Up to 2 decimals",
          ),
        unit: z.enum(["g", "kg", "oz", "lb"]),
      })
      .nullable()
      .optional(),
    dimensions: z
      .object({
        length: dimension,
        width: dimension,
        height: dimension,
        unit: z.enum(DimensionUnit),
      })
      .nullable()
      .optional(),
    manufacturerId: id.nullable().optional(),
    /** Creates the manufacturer and assigns it; overrides manufacturerId. */
    newManufacturer: newManufacturerSchema.optional(),
  }),
  output: z.object({ row: InternationalCustomsCatalogRow }),
};
